import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import {
    ACCOUNT_PAGE_SEND_CODE_BUTTON_SX,
    ACCOUNT_PAGE_SECTION_SX,
    ACCOUNT_PAGE_SUMMARY_SX,
} from "./accountPageStyles";

const PREMIUM_SURFACE = `
    linear-gradient(
        165deg,
        rgba(18,18,22,0.94) 0%,
        rgba(12,12,14,0.97) 52%,
        rgba(9,9,12,0.99) 100%
    )
`;

export const SUPPORT_TICKETS_SECTION_SX = {
    ...ACCOUNT_PAGE_SECTION_SX,
};

export const SUPPORT_TICKETS_HEADER_SX = {
    pb: "1.55rem",
    mb: "1.4rem",
    borderBottom: `1px solid ${colors.border.subtle}`,
};

export const SUPPORT_TICKETS_CONTROLS_SX = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: { xs: "column", sm: "row" },
    gap: "1rem",
    width: "100%",
    "& > a": {
        order: { xs: -1, sm: 0 },
    },
};

export const SUPPORT_TICKETS_NEW_BUTTON_SX = {
    ...ACCOUNT_PAGE_SEND_CODE_BUTTON_SX,
    ml: 0,
    minWidth: { xs: "100%", sm: "7.75rem" },
    height: "2.6rem",
    minHeight: "2.6rem",
    px: { xs: "1rem", sm: "0.95rem" },
    boxSizing: "border-box",
    textDecoration: "none",
};

export const SUPPORT_TICKETS_SEARCH_SX = {
    width: "100%",
    maxWidth: { xs: "100%", sm: "50%" },
    "& .MuiOutlinedInput-root": {
        height: "2.6rem",
        minHeight: "2.6rem",
        color: colors.text.primary,
        borderRadius: "12px",
        background: PREMIUM_SURFACE,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.035)",
        "& fieldset": {
            borderColor: colors.border.default,
        },
        "&:hover fieldset": {
            borderColor: alpha(colors.primary.main, 0.32),
        },
        "&.Mui-focused fieldset": {
            borderColor: colors.border.focus,
            borderWidth: "1px",
            boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.08)}`,
        },
    },
    "& input": {
        fontSize: "0.84rem",
    },
    "& input::placeholder": {
        color: colors.text.muted,
        opacity: 1,
    },
};

export const SUPPORT_TICKETS_FILTERS_SX = {
    display: { xs: "none", sm: "flex" },
    alignItems: "center",
    gap: "0.45rem",
    mt: 0,
    mb: "1rem",
    overflowX: "auto",
    pb: "0.15rem",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
        display: "none",
    },
};

export const getSupportTicketsFilterSx = (active) => ({
    flexShrink: 0,
    minHeight: "2rem",
    px: "0.85rem",
    py: "0.35rem",
    color: active ? colors.accent.soft : colors.text.muted,
    border: `1px solid ${
        active ? alpha(colors.primary.main, 0.3) : colors.border.subtle
    }`,
    borderRadius: "999px",
    background: active
        ? alpha(colors.primary.main, 0.1)
        : "rgba(255,255,255,0.018)",
    fontFamily: "inherit",
    fontSize: "0.72rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition:
        "color 0.15s ease, border-color 0.15s ease, background 0.15s ease",
    "&:hover": {
        color: colors.text.primary,
        borderColor: active
            ? alpha(colors.primary.main, 0.42)
            : colors.border.default,
        background: active
            ? alpha(colors.primary.main, 0.14)
            : "rgba(255,255,255,0.035)",
    },
});

export const SUPPORT_TICKETS_HISTORY_HEADER_SX = {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "1rem",
    mt: "2.35rem",
    mb: "0.65rem",
};

export const SUPPORT_TICKETS_HISTORY_TITLE_SX = {
    color: colors.text.primary,
    fontSize: "0.96rem",
    fontWeight: 750,
    letterSpacing: "0.01em",
};

export const SUPPORT_TICKETS_COUNT_SX = {
    color: colors.text.muted,
    fontSize: "0.72rem",
    fontWeight: 650,
    fontVariantNumeric: "tabular-nums",
};

export const SUPPORT_TICKETS_GRID_SX = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: "0.85rem",
};

export const SUPPORT_TICKET_CARD_SX = {
    ...ACCOUNT_PAGE_SUMMARY_SX,
    position: "relative",
    alignItems: "stretch",
    flexDirection: "column",
    gap: 0,
    minHeight: { xs: "auto", lg: "9.25rem" },
    px: { xs: "1rem", sm: "1.15rem" },
    py: { xs: "0.9rem", sm: "0.95rem" },
    color: "inherit",
    textAlign: "left",
    font: "inherit",
    cursor: "pointer",
    overflow: "hidden",
    transition:
        "transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
    "&::before": {
        content: '""',
        position: "absolute",
        top: "1rem",
        bottom: "1rem",
        left: 0,
        width: "2px",
        opacity: 0,
        borderRadius: "0 3px 3px 0",
        background: `linear-gradient(180deg, ${colors.accent.soft}, ${colors.primary.active})`,
        transition: "opacity 0.18s ease",
    },
    "&:hover, &:focus-visible": {
        outline: "none",
        transform: "translateY(-2px)",
        borderColor: alpha(colors.primary.main, 0.23),
        boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.05),
            0 16px 38px rgba(0,0,0,0.3)
        `,
        "&::before": {
            opacity: 1,
        },
    },
};

export const SUPPORT_TICKET_CARD_TOP_SX = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.75rem",
    mb: "0.65rem",
};

export const SUPPORT_TICKET_NUMBER_SX = {
    color: colors.text.muted,
    fontSize: "0.67rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    fontVariantNumeric: "tabular-nums",
    textTransform: "uppercase",
};

export const getSupportTicketStatusSx = (statusTone) => ({
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "flex-start",
    minHeight: "1.55rem",
    px: "0.62rem",
    color: statusTone.color,
    border: `1px solid ${statusTone.border}`,
    borderRadius: "999px",
    background: statusTone.background,
    fontSize: "0.66rem",
    fontWeight: 800,
    letterSpacing: "0.025em",
    lineHeight: 1,
    whiteSpace: "nowrap",
});

export const SUPPORT_TICKET_CARD_SUBJECT_SX = {
    color: colors.text.primary,
    fontSize: { xs: "0.95rem", sm: "1rem" },
    fontWeight: 750,
    lineHeight: 1.35,
    letterSpacing: "-0.005em",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
};

export const SUPPORT_TICKET_CARD_DESCRIPTION_SX = {
    mt: "0.45rem",
    mb: "0.8rem",
    color: colors.text.muted,
    fontSize: "0.77rem",
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
};

export const SUPPORT_TICKET_CARD_META_SX = {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.55rem 0.9rem",
    mt: "auto",
    pt: "0.7rem",
    borderTop: `1px solid ${colors.border.subtle}`,
};

export const SUPPORT_TICKET_META_ITEM_SX = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    minWidth: 0,
    color: colors.text.muted,
    fontSize: "0.68rem",
    lineHeight: 1.2,
    "& svg": {
        flexShrink: 0,
        color: alpha(colors.primary.main, 0.72),
        fontSize: "0.9rem",
    },
};

export const SUPPORT_TICKET_STATE_SX = {
    display: "flex",
    minHeight: "13rem",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "0.65rem",
    px: "1.5rem",
    py: "2.5rem",
    textAlign: "center",
    border: `1px solid ${colors.border.subtle}`,
    borderRadius: "12px",
    background: PREMIUM_SURFACE,
};

export const SUPPORT_TICKET_STATE_TITLE_SX = {
    color: colors.text.primary,
    fontSize: "0.93rem",
    fontWeight: 750,
};

export const SUPPORT_TICKET_STATE_COPY_SX = {
    maxWidth: "25rem",
    color: colors.text.muted,
    fontSize: "0.78rem",
    lineHeight: 1.5,
};

export const SUPPORT_TICKET_EMPTY_STATE_SX = {
    ...SUPPORT_TICKET_STATE_SX,
    minHeight: { xs: "15rem", sm: "17rem" },
    gap: "0.55rem",
    background: `
        radial-gradient(circle at 50% 38%, ${alpha(colors.primary.main, 0.07)}, transparent 34%),
        ${PREMIUM_SURFACE}
    `,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.025)",
};

export const SUPPORT_TICKET_EMPTY_ICON_SX = {
    display: "grid",
    placeItems: "center",
    width: "3rem",
    height: "3rem",
    mb: "0.3rem",
    color: colors.primary.active,
    border: `1px solid ${alpha(colors.primary.main, 0.16)}`,
    borderRadius: "10px",
    background: alpha(colors.primary.main, 0.045),
    "& svg": {
        fontSize: "1.55rem",
    },
};

export const SUPPORT_TICKET_EMPTY_ACTION_SX = {
    ...SUPPORT_TICKETS_NEW_BUTTON_SX,
    width: { xs: "100%", sm: "auto" },
    maxWidth: { xs: "17rem", sm: "none" },
    minWidth: { xs: 0, sm: "8.5rem" },
    mt: "1.15rem",
};

export const SUPPORT_TICKET_DRAWER_BACKDROP_SX = {
    position: "fixed",
    inset: 0,
    zIndex: 999,
    background: "rgba(0,0,0,0.36)",
    backdropFilter: "blur(3px)",
    WebkitBackdropFilter: "blur(3px)",
};

export const getSupportTicketDrawerSx = (open) => ({
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 1401,
    width: { xs: "100%", sm: "28rem" },
    maxWidth: { xs: "100%", sm: "28rem" },
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    transform: open ? "translateX(0)" : "translateX(100%)",
    opacity: open ? 1 : 0,
    pointerEvents: open ? "auto" : "none",
    transition: "transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s ease",
});

export const SUPPORT_TICKET_DRAWER_INNER_SX = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    p: 0,
    boxSizing: "border-box",
    background: colors.background.base,
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    borderLeft: `1px solid ${alpha(colors.primary.main, 0.18)}`,
    boxShadow: "-18px 0 44px rgba(0,0,0,0.48), inset 0 1px 0 rgba(255,255,255,0.04)",
    overflow: "hidden",
    position: "relative",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "2px",
        background: "linear-gradient(90deg, transparent, rgba(255,190,90,0.48), transparent)",
        pointerEvents: "none",
    },
};

export const SUPPORT_TICKET_CHAT_SHELL_SX = {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    border: "none",
    borderRadius: 0,
    background: "#07080A",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.035),
        inset 18px 0 36px rgba(0,0,0,0.2)
    `,
};

export const SUPPORT_TICKET_CHAT_HEADER_SX = {
    display: "flex",
    flexDirection: "column",
    gap: "0.8rem",
    px: { xs: "1rem", sm: "1.2rem" },
    pt: { xs: "0.85rem", sm: "0.95rem" },
    pb: { xs: "1rem", sm: "1.1rem" },
    borderBottom: `1px solid ${colors.border.subtle}`,
    background: `
        linear-gradient(
            135deg,
            rgba(255,255,255,0.035),
            rgba(232,163,61,0.035) 52%,
            rgba(8,8,11,0.02)
        )
    `,
};

export const SUPPORT_TICKET_CHAT_HEADER_TOP_SX = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.75rem",
};

export const SUPPORT_TICKET_CHAT_HEADER_BRAND_SX = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    minWidth: 0,
};

export const SUPPORT_TICKET_CHAT_HEADER_LOGO_SX = {
    width: "1.15rem",
    height: "1.15rem",
    objectFit: "contain",
};

export const SUPPORT_TICKET_CHAT_EYEBROW_SX = {
    color: alpha(colors.primary.main, 0.88),
    fontSize: "0.61rem",
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: "0.13em",
    textTransform: "uppercase",
};

export const SUPPORT_TICKET_CHAT_HEADER_MAIN_SX = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.75rem",
};

export const SUPPORT_TICKET_CHAT_HEADER_META_PANEL_SX = {
    width: "100%",
    px: "0.7rem",
    py: "0.48rem",
    boxSizing: "border-box",
    border: `1px solid ${colors.border.subtle}`,
    borderRadius: "8px",
    background: "rgba(0,0,0,0.18)",
};

export const SUPPORT_TICKET_CHAT_CLOSE_BUTTON_SX = {
    width: "1.9rem",
    height: "1.9rem",
    flexShrink: 0,
    mt: "-0.1rem",
    color: colors.text.secondary,
    borderRadius: "0.55rem",
    background: "rgba(255,255,255,0.04)",
    "&:hover": {
        color: colors.text.primary,
        background: "rgba(255,255,255,0.08)",
    },
};

export const SUPPORT_TICKET_CHAT_TITLE_SX = {
    color: colors.text.primary,
    fontSize: { xs: "1rem", sm: "1.05rem" },
    fontWeight: 780,
    lineHeight: 1.3,
    letterSpacing: "-0.015em",
    overflowWrap: "anywhere",
};

export const SUPPORT_TICKET_CHAT_META_SX = {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.35rem 0.7rem",
};

export const SUPPORT_TICKET_COPY_ID_BUTTON_SX = {
    width: "1.45rem",
    height: "1.45rem",
    p: 0,
    ml: "0.12rem",
    color: colors.primary.main,
    border: `1px solid ${alpha(colors.primary.main, 0.22)}`,
    borderRadius: "5px",
    background: "transparent",
    "& svg": {
        fontSize: "0.78rem",
    },
    "&:hover": {
        color: colors.accent.soft,
        background: alpha(colors.primary.main, 0.08),
    },
};

export const SUPPORT_TICKET_CONVERSATION_SX = {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    px: { xs: "0.85rem", sm: "1.35rem" },
    py: { xs: "1.1rem", sm: "1.4rem" },
    backgroundImage: `
        radial-gradient(circle at 86% 8%, rgba(232,163,61,0.065), transparent 29%),
        radial-gradient(circle at 4% 82%, rgba(255,255,255,0.025), transparent 34%),
        radial-gradient(circle, rgba(255,255,255,0.022) 0.7px, transparent 0.8px),
        linear-gradient(155deg, rgba(15,16,19,0.98), rgba(6,7,9,0.995) 62%)
    `,
    backgroundSize: "auto, auto, 24px 24px, auto",
    boxShadow: `
        inset 0 18px 30px rgba(0,0,0,0.24),
        inset 16px 0 30px rgba(0,0,0,0.12),
        inset -12px 0 28px rgba(0,0,0,0.2)
    `,
    scrollBehavior: "smooth",
    scrollbarColor: `${alpha(colors.primary.main, 0.25)} transparent`,
    "&::-webkit-scrollbar": {
        width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
        borderRadius: "999px",
        background: alpha(colors.primary.main, 0.2),
    },
};

export const SUPPORT_TICKET_DATE_DIVIDER_SX = {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    my: "0.35rem",
    mb: "1.15rem",
    color: colors.text.muted,
    fontSize: "0.64rem",
    fontWeight: 700,
    "&::before, &::after": {
        content: '""',
        height: "1px",
        flex: 1,
        background: colors.border.subtle,
    },
};

export const SUPPORT_TICKET_EVENT_SX = {
    display: "flex",
    justifyContent: "center",
    mb: "1.15rem",
    "& > span": {
        display: "inline-flex",
        alignItems: "center",
        gap: "0.38rem",
        px: "0.75rem",
        py: "0.38rem",
        color: colors.text.muted,
        border: `1px solid ${colors.border.subtle}`,
        borderRadius: "999px",
        background: "rgba(255,255,255,0.018)",
        fontSize: "0.65rem",
        fontWeight: 650,
        textAlign: "center",
    },
    "& svg": {
        color: colors.state.success.main,
        fontSize: "0.88rem",
    },
};

export const getSupportTicketMessageRowSx = (fromSupport) => ({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: fromSupport ? "flex-start" : "flex-end",
    gap: "0.65rem",
    mb: "1.15rem",
});

export const SUPPORT_TICKET_SUPPORT_AVATAR_SX = {
    display: { xs: "none", sm: "grid" },
    placeItems: "center",
    width: "2.25rem",
    height: "2.25rem",
    flexShrink: 0,
    color: colors.primary.main,
    border: `1px solid ${alpha(colors.primary.main, 0.2)}`,
    borderRadius: "50%",
    background: colors.background.dark,
    boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
    overflow: "hidden",
    "& img": {
        width: "1.25rem",
        height: "1.25rem",
        objectFit: "contain",
    },
};

export const SUPPORT_TICKET_PLAYER_AVATAR_SX = {
    ...SUPPORT_TICKET_SUPPORT_AVATAR_SX,
    color: colors.text.primary,
    borderColor: alpha(colors.text.primary, 0.12),
    background: "linear-gradient(145deg, rgba(30,30,34,0.98), rgba(13,13,16,0.98))",
    fontSize: "0.76rem",
    fontWeight: 800,
    "& img": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
};

export const getSupportTicketMessageBubbleSx = (fromSupport) => ({
    position: "relative",
    maxWidth: { xs: "88%", sm: "82%" },
    minWidth: { xs: 0, sm: "10rem" },
    px: { xs: "0.9rem", sm: "1rem" },
    pt: "0.78rem",
    pb: fromSupport ? "0.78rem" : "1.18rem",
    border: `1px solid ${
        fromSupport
            ? colors.border.default
            : alpha(colors.primary.main, 0.17)
    }`,
    borderRadius: fromSupport
        ? "5px 12px 12px 12px"
        : "12px 5px 12px 12px",
    background: fromSupport
        ? "linear-gradient(145deg, rgba(31,31,35,0.96), rgba(23,23,27,0.98))"
        : "linear-gradient(145deg, rgba(69,48,20,0.72), rgba(46,32,16,0.82))",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.025)",
});

export const SUPPORT_TICKET_MESSAGE_META_SX = {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.3rem",
    mb: "0.45rem",
    color: colors.text.muted,
    fontSize: "0.66rem",
    fontWeight: 700,
};

export const SUPPORT_TICKET_MESSAGE_TEXT_SX = {
    color: colors.text.secondary,
    fontSize: "0.78rem",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
};

export const SUPPORT_TICKET_MESSAGE_ATTACHMENTS_SX = {
    display: "grid",
    gap: "0.45rem",
    mt: "0.65rem",
};

export const SUPPORT_TICKET_MESSAGE_IMAGE_SX = {
    display: "block",
    width: "100%",
    maxHeight: { xs: "14rem", sm: "18rem" },
    objectFit: "cover",
    borderRadius: "8px",
    border: `1px solid ${alpha(colors.text.primary, 0.1)}`,
    background: "rgba(0,0,0,0.22)",
};

export const SUPPORT_TICKET_MESSAGE_ATTACHMENT_NAME_SX = {
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "flex-start",
    px: "0.65rem",
    py: "0.38rem",
    color: colors.text.secondary,
    border: `1px solid ${colors.border.subtle}`,
    borderRadius: "7px",
    background: "rgba(255,255,255,0.035)",
    fontSize: "0.7rem",
    fontWeight: 650,
    overflowWrap: "anywhere",
};

export const SUPPORT_TICKET_MESSAGE_SENT_SX = {
    position: "absolute",
    right: "0.52rem",
    bottom: "0.42rem",
    color: colors.text.muted,
    fontSize: "0.95rem",
};

export const SUPPORT_TICKET_COMPOSER_SX = {
    flexShrink: 0,
    px: { xs: "0.85rem", sm: "1.25rem" },
    pt: "0.45rem",
    pb: { xs: "0.75rem", sm: "0.85rem" },
    background: "transparent",
};

export const SUPPORT_TICKET_COMPOSER_FIELD_SX = {
    flex: 1,
    "& .MuiOutlinedInput-root": {
        minHeight: "3.25rem",
        alignItems: "flex-end",
        color: colors.text.primary,
        borderRadius: "12px",
        background: "linear-gradient(145deg, rgba(22,22,26,0.98), rgba(13,13,16,0.99))",
        boxShadow: "0 12px 30px rgba(0,0,0,0.32)",
        p: "0.55rem 0.45rem 0.55rem 1rem",
        "& fieldset": {
            borderColor: colors.border.default,
        },
        "&:hover fieldset": {
            borderColor: alpha(colors.primary.main, 0.22),
        },
        "&.Mui-focused fieldset": {
            borderColor: colors.primary.active,
            borderWidth: "1px",
        },
    },
    "& textarea": {
        minHeight: "1.1rem",
        alignSelf: "center",
        p: "0.18rem 0 !important",
        fontSize: "0.78rem",
        lineHeight: 1.38,
    },
    "& .MuiInputAdornment-root": {
        alignSelf: "flex-end",
        height: "2.15rem",
        maxHeight: "none",
        ml: "0.55rem",
    },
    "& textarea::placeholder": {
        color: colors.text.muted,
        opacity: 1,
    },
};

export const SUPPORT_TICKET_SEND_BUTTON_SX = {
    width: "2.15rem",
    height: "2.15rem",
    flexShrink: 0,
    color: colors.text.contrast,
    border: "1px solid rgba(255,210,120,0.25)",
    borderRadius: "9px",
    background: `
        linear-gradient(
            180deg,
            #E0A84A 0%,
            #C9851F 52%,
            #A66A14 100%
        )
    `,
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.25),
        0 8px 20px rgba(160,100,20,0.22)
    `,
    "&:hover": {
        filter: "brightness(1.06)",
        background: `
            linear-gradient(
                180deg,
                #E8B255 0%,
                #D8922E 52%,
                #B8741F 100%
            )
        `,
    },
    "&.Mui-disabled": {
        color: colors.text.muted,
        borderColor: colors.border.subtle,
        background: "rgba(255,255,255,0.035)",
        boxShadow: "none",
    },
};

export const SUPPORT_TICKET_CLOSED_SX = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.45rem",
    px: "1rem",
    py: "0.85rem",
    color: colors.text.muted,
    borderTop: `1px solid ${colors.border.subtle}`,
    background: "rgba(8,8,11,0.98)",
    fontSize: "0.72rem",
    fontWeight: 650,
    textAlign: "center",
    "& svg": {
        color: colors.state.success.main,
        fontSize: "1rem",
    },
};
